import { upsertFile } from '@commenda/api';
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Icons,
  Input,
  Label,
  SingleFileUploader,
} from '@commenda/kakashi';
import { LegacyDocumentType } from '@commenda/prisma';
import { FileBasic } from '@commenda/types/src/file/types';
import { useEffect, useRef, useState } from 'react';
import { PLACEHOLDER, SIMPLE_TYPES, TYPE_LABEL } from './helpers';
import type {
  BuilderFieldType,
  CustomFieldDraft,
  FieldDefinition,
} from './types';

const FileFieldInput = ({
  companyId,
  onChange,
}: {
  companyId: number;
  onChange: (value: { fileId: number } | undefined) => void;
}) => {
  const [file, setFile] = useState<FileBasic | null>(null);
  const mounted = useRef(true);
  const uploadSeq = useRef(0);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  return (
    <SingleFileUploader
      file={file as FileBasic}
      setFile={(next) => {
        setFile(next);
        // The uploader only fires onFileChange on upload; a removal must clear the value too.
        if (next === null) onChange(undefined);
      }}
      companyId={companyId}
      onFileChange={(uploaded) => {
        if (!uploaded.fileUrl) {
          onChange(undefined);
          return;
        }
        uploadSeq.current += 1;
        const seq = uploadSeq.current;
        upsertFile({
          companyId,
          documentType: LegacyDocumentType.Other,
          fileUrl: uploaded.fileUrl,
          fileName: uploaded.fileName,
        })
          .then((upserted) => {
            // Ignore an out-of-order upload or a resolution after unmount.
            if (!mounted.current || seq !== uploadSeq.current) return;
            onChange(upserted ? { fileId: upserted.id } : undefined);
          })
          .catch(() => {
            if (mounted.current && seq === uploadSeq.current) onChange(undefined);
          });
      }}
      accept={{
        'application/pdf': ['.pdf'],
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
      }}
    />
  );
};

const FieldValueInput = ({
  field,
  companyId,
  onChange,
}: {
  field: CustomFieldDraft;
  companyId: number;
  onChange: (value: unknown) => void;
}) => {
  if (field.type === 'DATE') {
    return (
      <DatePicker
        date={typeof field.value === 'string' ? field.value : undefined}
        setDate={(d) => onChange(d || undefined)}
        placeholder="Select date"
      />
    );
  }
  if (field.type === 'FILE') {
    return <FileFieldInput companyId={companyId} onChange={onChange} />;
  }
  const inputType =
    field.type === 'URL'
      ? 'url'
      : field.type === 'EMAIL'
        ? 'email'
        : field.type === 'PASSWORD'
          ? 'password'
          : 'text';
  return (
    <Input
      type={inputType}
      value={typeof field.value === 'string' ? field.value : ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      placeholder={PLACEHOLDER[field.type]}
    />
  );
};

export const CommittedField = ({
  field,
  companyId,
  onChange,
  onRemove,
}: {
  field: CustomFieldDraft;
  companyId: number;
  onChange: (patch: Partial<CustomFieldDraft>) => void;
  onRemove?: () => void;
}) => (
  <div className="flex flex-col gap-xs rounded-md border border-border-secondary bg-bg-primary p-3xl">
    <Label required={field.isRequired} optional={!field.isRequired}>
      {field.label}
    </Label>
    <div className="flex items-center gap-xl">
      <div className="min-w-0 flex-1">
        <FieldValueInput
          field={field}
          companyId={companyId}
          onChange={(value) => onChange({ value })}
        />
      </div>
      {onRemove && (
        <button
          type="button"
          aria-label="Remove field"
          onClick={onRemove}
          className="shrink-0 text-text-quaternary hover:text-text-error"
        >
          <Icons.Trash01 className="size-4" />
        </button>
      )}
    </div>
  </div>
);

export const DraftCard = ({
  definition,
  onPatch,
  onAdd,
  onDiscard,
}: {
  definition: FieldDefinition;
  onPatch: (patch: Partial<FieldDefinition>) => void;
  onAdd: () => void;
  onDiscard: () => void;
}) => (
  <div className="flex flex-col rounded-md border border-border-secondary bg-bg-secondary px-3xl shadow-sm">
    <div className="flex items-center gap-xl py-md">
      <div className="flex flex-1 items-center justify-between gap-md">
        <p className="txt-small-bold text-text-secondary">
          {TYPE_LABEL[definition.type]}
        </p>
        <label className="flex items-center gap-md">
          <Checkbox
            checked={definition.isRequired}
            onCheckedChange={(v) => onPatch({ isRequired: v === true })}
          />
          <span className="txt-small-medium text-text-secondary">Required</span>
        </label>
      </div>
      <button
        type="button"
        aria-label="Discard field"
        onClick={onDiscard}
        className="shrink-0 text-text-quaternary hover:text-text-primary"
      >
        <Icons.XClose className="size-4" />
      </button>
    </div>
    <div className="flex flex-col gap-md rounded-md border-[0.5px] border-border-primary bg-bg-primary p-lg">
      <p className="txt-small-regular text-text-placeholder">Add a label for this field</p>
      <Input
        value={definition.label}
        onChange={(e) => onPatch({ label: e.target.value })}
        placeholder="Enter text"
      />
    </div>
    <div className="flex items-center justify-between gap-md py-lg">
      <label className="flex items-center gap-md">
        <Checkbox
          checked={definition.saveForFuture}
          onCheckedChange={(v) => onPatch({ saveForFuture: v === true })}
        />
        <span className="txt-small-medium text-text-secondary">
          Save field for future entries
        </span>
      </label>
      <Button
        size="sm"
        onClick={onAdd}
        disabled={!/[A-Za-z0-9]/.test(definition.label)}
      >
        Add
      </Button>
    </div>
  </div>
);

export const AddFieldDropdown = ({
  onAdd,
}: {
  onAdd: (type: BuilderFieldType) => void;
}) => (
  <Dropdown
    align="start"
    menu={{
      groups: [
        {
          items: SIMPLE_TYPES.map((type) => ({
            label: TYPE_LABEL[type],
            onClick: () => onAdd(type),
          })),
        },
        {
          items: [
            {
              label: TYPE_LABEL.FILE,
              onClick: () => onAdd('FILE'),
            },
          ],
        },
      ],
    }}
  >
    <Button variant="secondary" size="sm">
      <Icons.Plus className="size-4" />
      <span>Add field</span>
    </Button>
  </Dropdown>
);
